/**
 * Cloudflare Turnstile widget (ADR-0018 §4).
 *
 * Renders the Turnstile widget when VITE_TURNSTILE_SITE_KEY is configured.
 * When the site key is absent (local dev, tests) the component immediately
 * calls back with an empty token so the form can proceed without a real
 * challenge — the server accepts any token when TURNSTILE_SECRET_KEY is also
 * unset.
 */
import { useEffect, useRef } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render(container: HTMLElement, options: TurnstileOptions): string;
      remove(widgetId: string): void;
      reset(widgetId: string): void;
    };
    onTurnstileLoad?: () => void;
  }
}

interface TurnstileOptions {
  sitekey: string;
  callback: (token: string) => void;
  'error-callback': () => void;
  'expired-callback': () => void;
  theme: 'light' | 'dark' | 'auto';
  size: 'normal' | 'compact';
}

interface TurnstileProps {
  onToken: (token: string) => void;
  onError?: () => void;
  onExpire?: () => void;
  /** Call reset() to request a fresh token (e.g., after a failed attempt). */
  resetKey?: number;
}

const SCRIPT_ID = 'cf-turnstile-script';
const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

export default function Turnstile({ onToken, onError, onExpire, resetKey }: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<string | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: resetKey is used as a trigger to force re-execution; it is intentionally listed despite not appearing in the effect body
  useEffect(() => {
    if (!SITE_KEY) {
      // Dev / test: bypass the widget and provide an empty token.
      onToken('');
      return;
    }

    function render() {
      if (!containerRef.current || !window.turnstile) return;
      if (widgetRef.current) {
        window.turnstile?.reset(widgetRef.current);
        return;
      }
      widgetRef.current = window.turnstile.render(containerRef.current, {
        sitekey: SITE_KEY as string,
        callback: onToken,
        'error-callback': () => onError?.(),
        'expired-callback': () => {
          onExpire?.();
          widgetRef.current = null;
        },
        theme: 'auto',
        size: 'normal',
      });
    }

    if (window.turnstile) {
      render();
    } else {
      window.onTurnstileLoad = render;
      if (!document.getElementById(SCRIPT_ID)) {
        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
    }

    return () => {
      if (widgetRef.current && window.turnstile) {
        window.turnstile.remove(widgetRef.current);
        widgetRef.current = null;
      }
    };
  }, [resetKey, onToken, onError, onExpire]);

  if (!SITE_KEY) return null;

  return <div ref={containerRef} role="presentation" />;
}
